import { Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowRightLeft, Gift, Settings2, ShieldCheck, Trash2, UserMinus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminLayout } from '@/app/shell/admin-layout';
import {
  type AdminTeamMember,
  type BillingRole,
  useAdminTeam,
  useAdminTeamMembers,
  useAssignBillingRole,
  usePatchTeamLimits,
  useRemoveBillingRole,
  useSoftDeleteTeam,
  useTransferOwnership,
} from '@/shared/api/admin-teams';
import { cn } from '@/shared/lib/cn';
import { CopyId } from '@/shared/ui/kit';
import { ReasonDialog } from '@/shared/ui/reason-dialog';

import { TeamUsageCard } from './team-usage-card';

export function TeamDetailPage({ id }: { id: string }) {
  const { data: team, isLoading, error } = useAdminTeam(id);

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const patchMut = usePatchTeamLimits();
  const transferMut = useTransferOwnership();
  const deleteMut = useSoftDeleteTeam();

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="text-sm text-neutral-500">Loading…</div>
      </AdminLayout>
    );
  }
  if (error || !team) {
    return (
      <AdminLayout>
        <div className="rounded-lg border border-red-900 bg-red-950/30 p-4 text-sm text-red-300">
          Team not found.
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Link
        to="/teams"
        className="mb-4 inline-flex items-center gap-1 text-sm text-neutral-400 hover:text-neutral-200"
      >
        <ArrowLeft size={14} /> All teams
      </Link>

      <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-100">{team.name}</h1>
          <p className="text-sm text-neutral-400">
            /{team.slug} · {team.kind}
            {team.archivedAt ? <span className="ml-2 text-amber-500">(archived)</span> : null}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Link
            to="/subscriptions/grant"
            search={{ customerType: 'team' as const, customerId: team.id }}
            className="inline-flex items-center gap-1.5 rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700"
          >
            <Gift size={14} /> Grant / change plan
          </Link>
          <Button onClick={() => setOverrideOpen(true)} icon={<Settings2 size={14} />}>
            Override limits
          </Button>
          <Button
            onClick={() => setTransferOpen(true)}
            icon={<ArrowRightLeft size={14} />}
            disabled={team.kind === 'personal'}
            title={team.kind === 'personal' ? 'Personal teams cannot transfer ownership' : undefined}
          >
            Transfer ownership
          </Button>
          {!team.archivedAt && team.kind !== 'personal' ? (
            <Button onClick={() => setDeleteOpen(true)} tone="danger" icon={<Trash2 size={14} />}>
              Soft delete
            </Button>
          ) : null}
        </div>
      </header>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card title="General">
          <Row label="Team ID" value={<CopyId value={team.id} label="team id" />} />
          <Row label="Slug" value={`/${team.slug}`} />
          <Row label="Kind" value={team.kind} />
          <Row label="Created" value={formatDateTime(team.createdAt)} />
          <Row
            label="Created by"
            value={
              <Link to="/users/$id" params={{ id: team.createdBy }} className="font-mono text-xs text-neutral-300 hover:text-orange-300">
                {team.createdBy.slice(0, 8)}…
              </Link>
            }
          />
          {team.archivedAt ? (
            <Row label="Archived" value={<span className="text-amber-400">{formatDateTime(team.archivedAt)}</span>} />
          ) : null}
        </Card>

        <Card title="Billing">
          <Row label="Plan" value={<span className="font-medium text-neutral-100">{team.billingPlan}</span>} />
          <Row label="Status" value={team.subscriptionStatus} />
          <Row
            label="Current period end"
            value={team.currentPeriodEnd ? formatDateTime(team.currentPeriodEnd) : '—'}
          />
          <Row label="Trial ends at" value={team.trialEndsAt ? formatDateTime(team.trialEndsAt) : '—'} />
        </Card>

        <TeamUsageCard team={team} />

        <Card title="Limit overrides">
          {Object.keys(team.limitOverrides).length === 0 ? (
            <p className="text-sm text-neutral-500">
              No overrides — team uses plan defaults from <Link to="/plans" className="text-orange-400 hover:underline">Plans</Link>.
            </p>
          ) : (
            <pre className="overflow-x-auto rounded bg-neutral-950 p-3 text-xs text-neutral-300">
              {JSON.stringify(team.limitOverrides, null, 2)}
            </pre>
          )}
        </Card>
      </section>

      {/* Members + Billing Roles section */}
      <MembersAndBillingRolesSection teamId={team.id} isPersonalTeam={team.kind === 'personal'} />

      {/* Override limits dialog (rich form via children slot of ReasonDialog) */}
      <OverrideLimitsDialog
        open={overrideOpen}
        onOpenChange={setOverrideOpen}
        teamId={team.id}
        currentOverrides={team.limitOverrides}
        pending={patchMut.isPending}
        onApply={async (input) => {
          await patchMut.mutateAsync({ id: team.id, ...input });
          toast.success('Limits updated');
          setOverrideOpen(false);
        }}
      />

      <TransferOwnershipDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        pending={transferMut.isPending}
        onConfirm={async (newOwnerUserId, reason) => {
          await transferMut.mutateAsync({ id: team.id, newOwnerUserId, reason });
          toast.success('Ownership transferred');
          setTransferOpen(false);
        }}
      />

      <ReasonDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Soft delete ${team.slug}?`}
        description="Sets archivedAt.  Data preserved for audit; team disappears from rosters.  Reversible by clearing archivedAt (Phase 7 ops)."
        confirmLabel="Soft delete"
        tone="danger"
        pending={deleteMut.isPending}
        onConfirm={async (reason) => {
          await deleteMut.mutateAsync({ id: team.id, reason });
          toast.success('Team soft-deleted');
          setDeleteOpen(false);
        }}
      />
    </AdminLayout>
  );
}

// ── Transfer ownership dialog ─────────────────────────────────────────────

function TransferOwnershipDialog(props: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  pending: boolean;
  onConfirm: (newOwnerUserId: string, reason: string) => Promise<void>;
}) {
  const [uid, setUid] = useState('');
  const [reason, setReason] = useState('');

  if (!props.open) return null;

  const uidTrim = uid.trim();
  const reasonTrim = reason.trim();
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uidTrim);
  const canSubmit = isUuid && reasonTrim.length >= 5 && !props.pending;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl bg-neutral-900 p-6 shadow-2xl ring-1 ring-neutral-800">
        <header className="mb-4">
          <h2 className="text-base font-semibold text-neutral-100">Transfer team ownership</h2>
          <p className="mt-1 text-sm text-neutral-400">
            Admin-initiated immediate transfer.  Existing OWNER(s) demoted to ADMIN.  Audit-logged.
          </p>
        </header>

        <label className="mb-1.5 block text-xs uppercase tracking-wider text-neutral-400">
          New owner user ID
        </label>
        <input
          value={uid}
          onChange={(e) => setUid(e.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000"
          className="w-full rounded-md bg-neutral-800 px-3 py-2 font-mono text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
        />
        {uid && !isUuid ? <div className="mt-1 text-xs text-red-400">Not a valid UUID.</div> : null}
        <div className="mt-1 text-xs text-neutral-500">
          Find the UUID in <Link to="/users" className="text-orange-400 hover:underline">Users</Link>.
        </div>

        <label className="mb-1.5 mt-4 block text-xs uppercase tracking-wider text-neutral-400">
          Reason <span className="text-neutral-600">(≥5 chars)</span>
        </label>
        <textarea
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Original founder left; transferring to co-founder."
          className="w-full resize-none rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
        />

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            onClick={() => props.onOpenChange(false)}
            className="rounded-md px-3 py-1.5 text-sm text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200"
          >
            Cancel
          </button>
          <button
            disabled={!canSubmit}
            onClick={() => void props.onConfirm(uidTrim, reasonTrim)}
            className="rounded-md bg-red-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50"
          >
            {props.pending ? 'Transferring…' : 'Transfer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Override limits dialog ────────────────────────────────────────────────

interface OverrideLimitsInput {
  mode: 'merge' | 'replace';
  overrides: {
    max_members?: number | null;
    max_projects?: number | null;
    storage_quota_bytes?: number;
    max_file_size_bytes?: number;
  };
  reason: string;
}

function OverrideLimitsDialog(props: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  teamId: string;
  currentOverrides: Record<string, unknown>;
  pending: boolean;
  onApply: (input: OverrideLimitsInput) => Promise<void>;
}) {
  // Pre-fill from existing overrides so admin sees current state.
  const cur = props.currentOverrides;
  const [maxMembers, setMaxMembers] = useState<string>(toFieldValue(cur.max_members));
  const [maxProjects, setMaxProjects] = useState<string>(toFieldValue(cur.max_projects));
  const [storageMb, setStorageMb] = useState<string>(
    typeof cur.storage_quota_bytes === 'number' ? String(Math.round(cur.storage_quota_bytes / 1024 / 1024)) : '',
  );
  const [fileSizeMb, setFileSizeMb] = useState<string>(
    typeof cur.max_file_size_bytes === 'number' ? String(Math.round(cur.max_file_size_bytes / 1024 / 1024)) : '',
  );
  const [reason, setReason] = useState('');

  const reasonTrim = reason.trim();
  const canSubmit = reasonTrim.length >= 5 && !props.pending;

  async function submit() {
    const overrides: OverrideLimitsInput['overrides'] = {};
    if (maxMembers !== '') overrides.max_members = maxMembers === 'unlimited' ? null : Number(maxMembers);
    if (maxProjects !== '') overrides.max_projects = maxProjects === 'unlimited' ? null : Number(maxProjects);
    if (storageMb !== '') overrides.storage_quota_bytes = Number(storageMb) * 1024 * 1024;
    if (fileSizeMb !== '') overrides.max_file_size_bytes = Number(fileSizeMb) * 1024 * 1024;
    await props.onApply({ mode: 'merge', overrides, reason: reasonTrim });
  }

  if (!props.open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl bg-neutral-900 p-6 shadow-2xl ring-1 ring-neutral-800">
        <header className="mb-4">
          <h2 className="text-base font-semibold text-neutral-100">Override limits</h2>
          <p className="mt-1 text-sm text-neutral-400">
            Set per-team override values.  Empty = use plan default.  Type
            <code className="mx-1 rounded bg-neutral-800 px-1 text-orange-400">unlimited</code> for member/project
            count to grant unlimited.  Audit-logged.
          </p>
        </header>

        <div className="grid grid-cols-2 gap-4">
          <NumField label="Max members" value={maxMembers} setValue={setMaxMembers} placeholder="10 or unlimited" allowUnlimited />
          <NumField label="Max projects" value={maxProjects} setValue={setMaxProjects} placeholder="unlimited" allowUnlimited />
          <NumField label="Storage quota (MB)" value={storageMb} setValue={setStorageMb} placeholder="500" />
          <NumField label="Max file size (MB)" value={fileSizeMb} setValue={setFileSizeMb} placeholder="25" />
        </div>

        <div className="mt-4">
          <label className="mb-1.5 block text-xs uppercase tracking-wider text-neutral-400">
            Reason <span className="text-neutral-600">(≥5 chars; recorded in audit log)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. Beta tester gets 5 GB storage for 90 days."
            className="w-full resize-none rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            onClick={() => props.onOpenChange(false)}
            className="rounded-md px-3 py-1.5 text-sm text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200"
          >
            Cancel
          </button>
          <button
            onClick={() => void submit()}
            disabled={!canSubmit}
            className="rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
          >
            {props.pending ? 'Applying…' : 'Apply overrides'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NumField(props: {
  label: string;
  value: string;
  setValue: (v: string) => void;
  placeholder: string;
  allowUnlimited?: boolean;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-xs uppercase tracking-wider text-neutral-400">{props.label}</label>
      <input
        value={props.value}
        onChange={(e) => props.setValue(e.target.value)}
        placeholder={props.placeholder}
        className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
      />
      {props.allowUnlimited ? (
        <div className="mt-1 text-xs text-neutral-600">number, or "unlimited"</div>
      ) : null}
    </div>
  );
}

function toFieldValue(v: unknown): string {
  if (v === null) return 'unlimited';
  if (typeof v === 'number') return String(v);
  return '';
}

// ── Shared primitives ─────────────────────────────────────────────────────

function Button(props: {
  children: React.ReactNode;
  onClick: () => void;
  tone?: 'default' | 'danger';
  icon?: React.ReactNode;
  disabled?: boolean;
  title?: string | undefined;
}) {
  const t = props.tone === 'danger' ? 'bg-red-700 hover:bg-red-800' : 'bg-neutral-800 hover:bg-neutral-700';
  return (
    <button
      onClick={props.onClick}
      disabled={props.disabled}
      title={props.title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-neutral-100 ring-1 ring-neutral-800 disabled:cursor-not-allowed disabled:opacity-40',
        t,
      )}
    >
      {props.icon}
      {props.children}
    </button>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-5">
      <h2 className="mb-3 text-sm font-medium text-neutral-300">{title}</h2>
      <dl className="space-y-2">{children}</dl>
    </section>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <dt className="text-neutral-500">{label}</dt>
      <dd className="text-right text-neutral-200">{value}</dd>
    </div>
  );
}


function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// ── Members + Billing Roles ──────────────────────────────────────────────────

/**
 * Members panel for the team detail page.  Surfaces every active member with
 * their team-role (membership.rolePreset) and their billing-role assignment
 * (Owner / Admin / Viewer / —).  Lets the operator assign, change, or remove
 * the billing role inline — each mutation routed through ReasonDialog so
 * audit log entries always carry a justification.
 *
 * Personal teams (single-member, can't invite) get a banner instead of the
 * panel — billing roles only make sense for multi-member teams.
 */
function MembersAndBillingRolesSection({
  teamId,
  isPersonalTeam,
}: {
  teamId: string;
  isPersonalTeam: boolean;
}) {
  const { data: members, isLoading } = useAdminTeamMembers(teamId);
  const assignMut = useAssignBillingRole();
  const removeMut = useRemoveBillingRole();

  const [assignTarget, setAssignTarget] = useState<{
    member: AdminTeamMember;
    nextRole: BillingRole;
  } | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AdminTeamMember | null>(null);

  if (isPersonalTeam) {
    return (
      <section className="mt-6 rounded-lg border border-neutral-800 bg-neutral-900 p-5">
        <h2 className="mb-2 text-sm font-medium text-neutral-300">Members &amp; billing roles</h2>
        <p className="text-sm text-neutral-500">
          Personal teams have a single member.  Billing roles aren't applicable here — the team's
          subscription belongs to the owner directly.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-6 rounded-lg border border-neutral-800 bg-neutral-900 p-5">
      <header className="mb-3 flex items-baseline justify-between">
        <h2 className="text-sm font-medium text-neutral-300">Members &amp; billing roles</h2>
        <p className="text-xs text-neutral-500">
          {members ? `${members.length} active member${members.length === 1 ? '' : 's'}` : ''}
        </p>
      </header>

      <p className="mb-4 text-xs text-neutral-500">
        Billing roles control who in this team can view invoices, change plan, manage payment method,
        and cancel the subscription.  See <code className="text-neutral-400">docs/24 §7</code> for
        the role × action matrix.  Each change is audit-logged with a reason.
      </p>

      {isLoading ? (
        <div className="py-6 text-center text-sm text-neutral-500">Loading members…</div>
      ) : !members || members.length === 0 ? (
        <div className="py-6 text-center text-sm text-neutral-500">No active members.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-neutral-800">
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/40 text-left text-[10px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-3 py-2 font-medium">Member</th>
                <th className="px-3 py-2 font-medium">Team role</th>
                <th className="px-3 py-2 font-medium">Billing role</th>
                <th className="px-3 py-2 font-medium">Joined</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {members.map((m) => (
                <tr key={m.userId} className="hover:bg-neutral-800/30">
                  <td className="px-3 py-2.5">
                    <Link to="/users/$id" params={{ id: m.userId }} className="block">
                      <div className="font-medium text-neutral-100">{m.name}</div>
                      <div className="text-xs text-neutral-400">
                        {m.email} · @{m.username}
                      </div>
                    </Link>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-neutral-300">
                    <code className="rounded bg-neutral-800 px-1.5 py-0.5">{m.teamRole}</code>
                  </td>
                  <td className="px-3 py-2.5">
                    <BillingRoleSelect
                      current={m.billingRole}
                      onChange={(nextRole) => setAssignTarget({ member: m, nextRole })}
                    />
                    {m.billingRoleAssignedAt ? (
                      <div className="mt-0.5 text-[10px] text-neutral-600">
                        since {new Date(m.billingRoleAssignedAt).toLocaleDateString()}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-neutral-500">
                    {new Date(m.joinedAt).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {m.billingRole ? (
                      <button
                        onClick={() => setRemoveTarget(m)}
                        className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300"
                        title="Remove billing role"
                      >
                        <UserMinus size={11} /> Remove role
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {assignTarget ? (
        <ReasonDialog
          open={true}
          onOpenChange={(o) => !o && setAssignTarget(null)}
          title={
            assignTarget.member.billingRole
              ? `Change ${assignTarget.member.name}'s billing role?`
              : `Assign ${assignTarget.member.name} as billing ${assignTarget.nextRole}?`
          }
          description={`Role: ${assignTarget.member.billingRole ?? '—'} → ${assignTarget.nextRole}.  All billing-related actions by this member will be audit-attributed to this assignment until removed.`}
          confirmLabel={assignTarget.member.billingRole ? 'Change role' : 'Assign'}
          tone="default"
          pending={assignMut.isPending}
          onConfirm={async (reason) => {
            await assignMut.mutateAsync({
              teamId,
              userId: assignTarget.member.userId,
              role: assignTarget.nextRole,
              reason,
            });
            toast.success(
              assignTarget.member.billingRole
                ? `Role changed to ${assignTarget.nextRole}`
                : `Assigned as billing ${assignTarget.nextRole}`,
            );
            setAssignTarget(null);
          }}
        >
          <div className="rounded-md bg-neutral-950 px-3 py-2 ring-1 ring-neutral-800">
            <div className="flex items-center gap-2 text-xs text-neutral-400">
              <ShieldCheck size={12} className="text-orange-400" />
              <span className="uppercase tracking-wider">{billingRoleSummary(assignTarget.nextRole)}</span>
            </div>
          </div>
        </ReasonDialog>
      ) : null}

      {removeTarget ? (
        <ReasonDialog
          open={true}
          onOpenChange={(o) => !o && setRemoveTarget(null)}
          title={`Remove ${removeTarget.name}'s billing role?`}
          description={`Was: ${removeTarget.billingRole}.  After removal they keep their team membership but lose billing-surface access.  Audit-logged.`}
          confirmLabel="Remove role"
          tone="danger"
          pending={removeMut.isPending}
          onConfirm={async (reason) => {
            await removeMut.mutateAsync({ teamId, userId: removeTarget.userId, reason });
            toast.success('Billing role removed');
            setRemoveTarget(null);
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * Inline selector for a member's billing role.  Native <select> so we don't
 * pull a Radix dropdown for one piece of UI — keyboard-accessible, screen-
 * reader friendly, and small.
 *
 * Picking the same role that's already set is a no-op (the surrounding
 * component compares before opening the assign dialog).
 */
function BillingRoleSelect({
  current,
  onChange,
}: {
  current: BillingRole | null;
  onChange: (next: BillingRole) => void;
}) {
  return (
    <select
      value={current ?? ''}
      onChange={(e) => {
        const v = e.target.value as BillingRole | '';
        if (v && v !== current) onChange(v);
      }}
      className={cn(
        'rounded-md bg-neutral-800 px-2 py-1 text-xs outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500',
        current === null
          ? 'text-neutral-500'
          : current === 'owner'
            ? 'text-orange-300'
            : current === 'admin'
              ? 'text-blue-300'
              : 'text-neutral-300',
      )}
    >
      <option value="" disabled>
        — none —
      </option>
      <option value="owner">Owner</option>
      <option value="admin">Admin</option>
      <option value="viewer">Viewer</option>
    </select>
  );
}

function billingRoleSummary(role: BillingRole): string {
  switch (role) {
    case 'owner':
      return 'Owner — change plan, manage payment, cancel, view invoices';
    case 'admin':
      return 'Admin — change plan, manage payment, view invoices (cannot cancel)';
    case 'viewer':
      return 'Viewer — read-only access to invoices';
  }
}
